import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, ArrowUpCircle, ArrowDownCircle, Trash2, Wallet, TrendingUp, TrendingDown, DollarSign,
  ArrowLeftRight, ShieldCheck, ShieldX, CheckCircle, AlertCircle, Import, RefreshCw, Check
} from 'lucide-react';
import { api, formatCurrency, formatDate } from '../lib/utils';
import { toast } from 'sonner';

const ENTRY_TYPES = [
  { value: 'capital_in', label: 'Capital In (Investment)', icon: ArrowUpCircle, color: 'text-green-600', bgColor: 'bg-green-50' },
  { value: 'capital_out', label: 'Capital Out (Withdrawal)', icon: ArrowDownCircle, color: 'text-red-600', bgColor: 'bg-red-50' },
  { value: 'expense_from_capital', label: 'Expense from Capital', icon: TrendingDown, color: 'text-orange-600', bgColor: 'bg-orange-50' },
  { value: 'asset_purchase', label: 'Asset Purchase', icon: DollarSign, color: 'text-blue-600', bgColor: 'bg-blue-50' },
  { value: 'adjustment', label: 'Adjustment', icon: TrendingUp, color: 'text-purple-600', bgColor: 'bg-purple-50' },
];

export default function InvestmentLedger() {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState({ total_inflow: 0, total_outflow: 0, current_capital: 0 });
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('ledger');
  const [selectedEntryIds, setSelectedEntryIds] = useState([]);
  
  // Pending items from other collections
  const [pendingInvestments, setPendingInvestments] = useState([]);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  
  const [formData, setFormData] = useState({
    entry_type: 'capital_in',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    notes: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/investment-ledger');
      setEntries(res.data.entries || []);
      setSummary(res.data.summary || { total_inflow: 0, total_outflow: 0, current_capital: 0 });
    } catch (err) {
      toast.error('Failed to load ledger');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingItems = async () => {
    setLoadingPending(true);
    try {
      const [investmentsRes, transfersRes] = await Promise.all([
        api.get('/investments').catch(() => ({ data: [] })),
        api.get('/transfers').catch(() => ({ data: { transfers: [] } }))
      ]);
      
      // Get existing ledger entries to filter out already imported items
      const ledgerRes = await api.get('/investment-ledger');
      const existingEntries = ledgerRes.data.entries || [];
      const linkedInvestmentIds = new Set(existingEntries.filter(e => e.linked_investment_id).map(e => e.linked_investment_id));
      const linkedTransferIds = new Set(existingEntries.filter(e => e.linked_transfer_id).map(e => e.linked_transfer_id));
      
      // Filter investments that haven't been imported yet
      const pendingInv = (investmentsRes.data || []).filter(inv => !linkedInvestmentIds.has(inv.investment_id));
      setPendingInvestments(pendingInv);
      
      // Filter transfers that haven't been imported yet
      const allTransfers = transfersRes.data.transfers || [];
      const pendingTfr = allTransfers.filter(tfr => !linkedTransferIds.has(tfr.transfer_id));
      setPendingTransfers(pendingTfr);
    } catch (err) {
      console.error('Failed to load pending items', err);
    } finally {
      setLoadingPending(false);
    }
  };

  useEffect(() => { 
    fetchData(); 
    fetchPendingItems();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.description) {
      toast.error('Amount and description are required');
      return;
    }
    try {
      await api.post('/investment-ledger', {
        ...formData,
        amount: parseFloat(formData.amount)
      });
      toast.success('Entry added successfully');
      setAddDialogOpen(false);
      setFormData({
        entry_type: 'capital_in',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        description: '',
        notes: ''
      });
      fetchData();
    } catch (err) {
      toast.error('Failed to add entry');
    }
  };

  const handleDelete = async (entryId) => {
    if (!confirm('Delete this entry?')) return;
    try {
      await api.delete(`/investment-ledger/${entryId}`);
      toast.success('Entry deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleToggleVerification = async (entryId, currentStatus) => {
    try {
      await api.patch(`/investment-ledger/${entryId}`, {
        verification_status: currentStatus === 'verified' ? 'unverified' : 'verified'
      });
      toast.success(currentStatus === 'verified' ? 'Marked as unverified' : 'Verified successfully');
      fetchData();
    } catch (err) {
      toast.error('Failed to update verification status');
    }
  };

  const handleImportInvestment = async (investment) => {
    try {
      await api.post('/investment-ledger/import-investment', {
        investment_id: investment.investment_id
      });
      toast.success(`Imported: ${investment.name || investment.type}`);
      fetchData();
      fetchPendingItems();
    } catch (err) {
      toast.error('Failed to import investment');
    }
  };

  const handleImportTransfer = async (transfer) => {
    try {
      await api.post('/investment-ledger/import-transfer', {
        transfer_id: transfer.transfer_id
      });
      toast.success(`Imported transfer: ${formatCurrency(transfer.amount)}`);
      fetchData();
      fetchPendingItems();
    } catch (err) {
      toast.error('Failed to import transfer');
    }
  };

  const handleImportAllInvestments = async () => {
    if (pendingInvestments.length === 0) return;
    try {
      for (const inv of pendingInvestments) {
        await api.post('/investment-ledger/import-investment', {
          investment_id: inv.investment_id
        });
      }
      toast.success(`Imported ${pendingInvestments.length} investments`);
      fetchData();
      fetchPendingItems();
    } catch (err) {
      toast.error('Failed to import all investments');
    }
  };

  const handleImportAllTransfers = async () => {
    if (pendingTransfers.length === 0) return;
    try {
      for (const tfr of pendingTransfers) {
        await api.post('/investment-ledger/import-transfer', {
          transfer_id: tfr.transfer_id
        });
      }
      toast.success(`Imported ${pendingTransfers.length} transfers`);
      fetchData();
      fetchPendingItems();
    } catch (err) {
      toast.error('Failed to import all transfers');
    }
  };

  const getEntryType = (type) => ENTRY_TYPES.find(t => t.value === type) || ENTRY_TYPES[0];

  const verifiedCount = entries.filter(e => e.verification_status === 'verified').length;
  const unverifiedCount = entries.filter(e => e.verification_status !== 'verified').length;
  const unverifiedEntries = entries.filter(e => e.verification_status !== 'verified');

  const toggleSelectEntry = (entryId) => {
    if (selectedEntryIds.includes(entryId)) {
      setSelectedEntryIds(selectedEntryIds.filter(id => id !== entryId));
    } else {
      setSelectedEntryIds([...selectedEntryIds, entryId]);
    }
  };

  const selectAllUnverified = () => {
    const ids = unverifiedEntries.map(e => e.entry_id);
    setSelectedEntryIds(ids);
  };

  const handleBulkVerify = async () => {
    if (selectedEntryIds.length === 0) {
      toast.error('Select entries to verify');
      return;
    }
    try {
      const res = await api.post('/investment-ledger/bulk-verify', {
        entry_ids: selectedEntryIds
      });
      toast.success(res.data?.message || `Verified ${selectedEntryIds.length} entries`);
      setSelectedEntryIds([]);
      fetchData();
    } catch (err) {
      console.error('Bulk verify error:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to bulk verify. Please deploy latest changes.';
      toast.error(errorMsg);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto" data-testid="investment-ledger-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">Capital Ledger</h1>
          <p className="text-zinc-500 mt-1">Track capital inflows, outflows, and verify entries</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedEntryIds.length > 0 && (
            <Button onClick={handleBulkVerify} className="bg-green-600 hover:bg-green-700" data-testid="bulk-verify-ledger-btn">
              <ShieldCheck className="w-4 h-4 mr-2" />
              Verify Selected ({selectedEntryIds.length})
            </Button>
          )}
          {unverifiedCount > 0 && selectedEntryIds.length === 0 && (
            <Button onClick={selectAllUnverified} variant="outline" className="text-amber-600 border-amber-200" data-testid="select-unverified-ledger-btn">
              <ShieldX className="w-4 h-4 mr-2" />
              Select Unverified ({unverifiedCount})
            </Button>
          )}
          <Button onClick={() => setAddDialogOpen(true)} data-testid="add-entry-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Entry
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500">Total Capital In</p>
                <p className="text-xl font-bold text-green-600">{formatCurrency(summary.total_inflow)}</p>
              </div>
              <ArrowUpCircle className="w-8 h-8 text-green-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500">Total Capital Out</p>
                <p className="text-xl font-bold text-red-600">{formatCurrency(summary.total_outflow)}</p>
              </div>
              <ArrowDownCircle className="w-8 h-8 text-red-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500">Current Balance</p>
                <p className={`text-xl font-bold ${summary.current_capital >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                  {formatCurrency(summary.current_capital)}
                </p>
              </div>
              <Wallet className="w-8 h-8 text-blue-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500">Verified Entries</p>
                <p className="text-xl font-bold text-emerald-600">{verifiedCount}</p>
              </div>
              <ShieldCheck className="w-8 h-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-zinc-500">Pending Review</p>
                <p className="text-xl font-bold text-amber-600">{unverifiedCount + pendingInvestments.length + pendingTransfers.length}</p>
              </div>
              <AlertCircle className="w-8 h-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="ledger" className="flex items-center gap-2">
            <Wallet className="w-4 h-4" />
            Ledger Entries
            {unverifiedCount > 0 && <span className="ml-1 text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{unverifiedCount}</span>}
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-2" data-testid="pending-tab">
            <Import className="w-4 h-4" />
            Pending Import
            {(pendingInvestments.length + pendingTransfers.length) > 0 && (
              <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                {pendingInvestments.length + pendingTransfers.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Ledger Entries Tab */}
        <TabsContent value="ledger">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Wallet className="w-5 h-5" />
                Capital Ledger
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8 text-zinc-500">Loading...</div>
              ) : entries.length === 0 ? (
                <div className="text-center py-12 text-zinc-500">
                  <Wallet className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
                  <p className="font-medium">No ledger entries yet</p>
                  <p className="text-sm mt-1">Add entries manually or import from Investments & Transfers</p>
                  <Button onClick={() => setActiveTab('pending')} variant="outline" className="mt-4">
                    <Import className="w-4 h-4 mr-2" />
                    Import Pending Items
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="pb-3 w-10"></th>
                        <th className="pb-3 text-xs font-medium text-zinc-500 uppercase">Date</th>
                        <th className="pb-3 text-xs font-medium text-zinc-500 uppercase">Type</th>
                        <th className="pb-3 text-xs font-medium text-zinc-500 uppercase">Description</th>
                        <th className="pb-3 text-xs font-medium text-zinc-500 uppercase text-center">Status</th>
                        <th className="pb-3 text-xs font-medium text-zinc-500 uppercase text-center">Inflow</th>
                        <th className="pb-3 text-xs font-medium text-zinc-500 uppercase text-center">Outflow</th>
                        <th className="pb-3 text-xs font-medium text-zinc-500 uppercase w-24"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((entry, idx) => {
                        const entryType = getEntryType(entry.entry_type);
                        const Icon = entryType.icon;
                        const isInflow = entry.flow === 'inflow';
                        const isVerified = entry.verification_status === 'verified';
                        const isLinked = entry.linked_investment_id || entry.linked_transfer_id;
                        return (
                          <tr key={entry.entry_id || idx} className={`border-b hover:bg-zinc-50 ${isVerified ? 'bg-green-50/30' : ''}`} data-testid={`ledger-entry-${entry.entry_id}`}>
                            <td className="py-3">
                              {!isVerified && (
                                <input 
                                  type="checkbox" 
                                  checked={selectedEntryIds.includes(entry.entry_id)}
                                  onChange={() => toggleSelectEntry(entry.entry_id)}
                                  className="w-4 h-4 rounded border-zinc-300 text-green-600 focus:ring-green-500"
                                />
                              )}
                            </td>
                            <td className="py-3 text-sm">{formatDate(entry.date)}</td>
                            <td className="py-3">
                              <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${entryType.bgColor} ${entryType.color}`}>
                                <Icon className="w-3 h-3" />
                                {entryType.label}
                              </div>
                              {isLinked && (
                                <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-50 text-cyan-600 border border-cyan-200">
                                  {entry.linked_investment_id ? 'Investment' : 'Transfer'}
                                </span>
                              )}
                            </td>
                            <td className="py-3 text-sm font-medium">
                              {entry.description}
                              {entry.notes && <span className="text-zinc-400 text-xs ml-2">({entry.notes})</span>}
                            </td>
                            <td className="py-3 text-center">
                              <button
                                onClick={() => handleToggleVerification(entry.entry_id, entry.verification_status)}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium cursor-pointer transition-colors ${
                                  isVerified
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                }`}
                                data-testid={`verify-toggle-${entry.entry_id}`}
                              >
                                {isVerified ? (
                                  <><ShieldCheck className="w-3 h-3" /> Verified</>
                                ) : (
                                  <><ShieldX className="w-3 h-3" /> Unverified</>
                                )}
                              </button>
                            </td>
                            <td className="py-3 text-center">
                              {isInflow ? (
                                <span className="text-green-600 font-medium">{formatCurrency(entry.amount)}</span>
                              ) : '-'}
                            </td>
                            <td className="py-3 text-center">
                              {!isInflow ? (
                                <span className="text-red-600 font-medium">{formatCurrency(entry.amount)}</span>
                              ) : '-'}
                            </td>
                            <td className="py-3">
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-zinc-400 hover:text-red-500" onClick={() => handleDelete(entry.entry_id)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pending Import Tab */}
        <TabsContent value="pending">
          <div className="space-y-6">
            {/* Pending Investments */}
            <Card>
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  Investments ({pendingInvestments.length} pending)
                </CardTitle>
                {pendingInvestments.length > 0 && (
                  <Button size="sm" onClick={handleImportAllInvestments} data-testid="import-all-investments">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Import All
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {loadingPending ? (
                  <div className="text-center py-6 text-zinc-500">
                    <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />
                    Loading...
                  </div>
                ) : pendingInvestments.length === 0 ? (
                  <div className="text-center py-6 text-zinc-500">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 text-green-200" />
                    <p className="text-sm">All investments imported</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="pb-2 text-left text-xs font-medium text-zinc-500 uppercase">Date</th>
                          <th className="pb-2 text-left text-xs font-medium text-zinc-500 uppercase">Name/Type</th>
                          <th className="pb-2 text-right text-xs font-medium text-zinc-500 uppercase">Amount</th>
                          <th className="pb-2 text-right text-xs font-medium text-zinc-500 uppercase">Current Value</th>
                          <th className="pb-2 w-20"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingInvestments.map((inv) => (
                          <tr key={inv.investment_id} className="border-b hover:bg-zinc-50" data-testid={`pending-inv-${inv.investment_id}`}>
                            <td className="py-2 text-zinc-500">{formatDate(inv.date)}</td>
                            <td className="py-2 font-medium">
                              {inv.name || inv.type}
                              <span className="ml-2 text-xs text-zinc-400 capitalize">{inv.type?.replace(/_/g, ' ')}</span>
                            </td>
                            <td className="py-2 text-right text-green-600 font-medium">{formatCurrency(inv.amount)}</td>
                            <td className="py-2 text-right">{formatCurrency(inv.current_value || inv.amount)}</td>
                            <td className="py-2 text-right">
                              <Button size="sm" variant="outline" onClick={() => handleImportInvestment(inv)} data-testid={`import-inv-${inv.investment_id}`}>
                                <Import className="w-3.5 h-3.5 mr-1" />
                                Import
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending Transfers */}
            <Card>
              <CardHeader className="pb-3 flex flex-row items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <ArrowLeftRight className="w-5 h-5 text-cyan-600" />
                  Inter-Account Transfers ({pendingTransfers.length} pending)
                </CardTitle>
                {pendingTransfers.length > 0 && (
                  <Button size="sm" onClick={handleImportAllTransfers} data-testid="import-all-transfers">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Import All
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                {loadingPending ? (
                  <div className="text-center py-6 text-zinc-500">
                    <RefreshCw className="w-5 h-5 mx-auto mb-2 animate-spin" />
                    Loading...
                  </div>
                ) : pendingTransfers.length === 0 ? (
                  <div className="text-center py-6 text-zinc-500">
                    <CheckCircle className="w-10 h-10 mx-auto mb-2 text-cyan-200" />
                    <p className="text-sm">All transfers imported</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="pb-2 text-left text-xs font-medium text-zinc-500 uppercase">Date</th>
                          <th className="pb-2 text-left text-xs font-medium text-zinc-500 uppercase">Description</th>
                          <th className="pb-2 text-center text-xs font-medium text-zinc-500 uppercase">Type</th>
                          <th className="pb-2 text-right text-xs font-medium text-zinc-500 uppercase">Amount</th>
                          <th className="pb-2 w-20"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {pendingTransfers.map((tfr) => (
                          <tr key={tfr.transfer_id} className="border-b hover:bg-zinc-50" data-testid={`pending-tfr-${tfr.transfer_id}`}>
                            <td className="py-2 text-zinc-500">{formatDate(tfr.date)}</td>
                            <td className="py-2 font-medium truncate max-w-[300px]" title={tfr.description}>{tfr.description}</td>
                            <td className="py-2 text-center">
                              <span className={`text-xs px-2 py-0.5 rounded-full ${tfr.type === 'credit' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'}`}>
                                {tfr.type === 'credit' ? 'Credit' : 'Debit'}
                              </span>
                            </td>
                            <td className={`py-2 text-right font-medium ${tfr.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                              {tfr.type === 'credit' ? '+' : '-'}{formatCurrency(tfr.amount)}
                            </td>
                            <td className="py-2 text-right">
                              <Button size="sm" variant="outline" onClick={() => handleImportTransfer(tfr)} data-testid={`import-tfr-${tfr.transfer_id}`}>
                                <Import className="w-3.5 h-3.5 mr-1" />
                                Import
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add Entry Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add Ledger Entry
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pt-4">
            <div>
              <Label>Entry Type</Label>
              <Select value={formData.entry_type} onValueChange={(v) => setFormData({ ...formData, entry_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="entry-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENTRY_TYPES.map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <span className={`flex items-center gap-2 ${type.color}`}>
                        <type.icon className="w-4 h-4" />
                        {type.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Amount</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  placeholder="0.00"
                  className="mt-1.5"
                  required
                  data-testid="amount-input"
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="mt-1.5"
                  required
                  data-testid="date-input"
                />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="e.g., Initial capital investment"
                className="mt-1.5"
                required
                data-testid="description-input"
              />
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional details..."
                className="mt-1.5"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1" data-testid="submit-entry-btn">
                Add Entry
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
